import React, { useState, useMemo } from 'react';
import {
  INVENTORY_CATEGORIES,
  EQUIPPABLE_CATEGORIES,
  CURRENCY_DENOMS,
  totalInGp,
  catalogFor,
  createItem,
  INVENTORY_CATALOG,
} from '../../lib/inventory';
import {
  currentWeight, carryCapacity, encumbranceTier, ENCUMBRANCE_LABELS,
} from '../../lib/inventory';
import EquipTab from './EquipTab';

// Modal-style overlay used by both PlayerView and the DM TokenInspector. The
// caller owns the data; this component is a controlled view that emits
// onInventoryChange / onCurrencyChange with the next array/object.
export default function InventoryPanel({
  inventory = [],
  currency = {},
  onInventoryChange,
  onCurrencyChange,
  editable = true,
  title = '🎒 Inventory',
  onClose,
  onTrade,        // optional — when provided, renders a 🤝 Trade button in the header
  // The two below are needed for Inventory v2 mechanical hooks. Optional
  // (caller may render a read-only inventory in DM TokenInspector without them).
  onEquip,        // (itemId) => void — called when the player toggles equip in EquipTab
  onUseConsumable,// (itemId) => void — called when the player taps "🍶 Use" on a consumable
  playerRecord,   // full player record — needed by the EquipTab to render AC, etc.
}) {
  const [activeTab, setActiveTab] = useState(INVENTORY_CATEGORIES[0].id);
  const [editingId, setEditingId] = useState(null);
  const [adding,    setAdding]    = useState(false); // true → show picker for activeTab
  const [customMode,setCustomMode]= useState(false); // true → custom form inside picker

  const tabItems = useMemo(
    () => inventory.filter((it) => it.category === activeTab),
    [inventory, activeTab],
  );

  // ── Currency edits ──────────────────────────────────────────────────────────
  const setCoin = (id, value) => {
    if (!onCurrencyChange) return;
    const next = { ...currency, [id]: Math.max(0, value | 0) };
    onCurrencyChange(next);
  };

  // ── Inventory edits ─────────────────────────────────────────────────────────
  const upsertItem = (item) => {
    if (!onInventoryChange) return;
    const idx = inventory.findIndex((it) => it.id === item.id);
    const next = idx >= 0
      ? inventory.map((it, i) => (i === idx ? item : it))
      : [...inventory, item];
    onInventoryChange(next);
  };

  const removeItem = (id) => {
    if (!onInventoryChange) return;
    onInventoryChange(inventory.filter((it) => it.id !== id));
  };

  const addFromCatalog = (entry) => {
    // Copy weight and any structured fields (weapon / armor / use) from the
    // catalog row so encumbrance + EquipTab mechanics work on this new item.
    // Previously this only copied name/description, leaving armor + weapons
    // weightless and the EquipTab unable to pull AC / damage info.
    upsertItem(createItem(entry.category, {
      name: entry.name,
      description: entry.description || '',
      weightLb: entry.weightLb ?? 0,
      equipped: false,
      qty: 1,
      ...(entry.weapon ? { weapon: entry.weapon } : {}),
      ...(entry.armor  ? { armor:  entry.armor  } : {}),
      ...(entry.use    ? { use:    entry.use    } : {}),
    }));
    setAdding(false);
    setCustomMode(false);
  };

  const addCustom = (fields) => {
    upsertItem(createItem(activeTab, fields));
    setAdding(false);
    setCustomMode(false);
  };

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <span style={{ flex: 1 }} />
          {editable && onTrade && (
            <button style={styles.tradeBtn} onClick={onTrade} title="Propose a trade with another player">
              🤝 Trade
            </button>
          )}
          {onClose && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
        </div>

        {/* Wallet — always visible above tabs */}
        <Wallet currency={currency} editable={editable} setCoin={setCoin} />
        <CarryMeter
          inventory={inventory}
          strScore={playerRecord?.abilityScores?.str ?? 10}
          sizeCategory={playerRecord?.sizeCategory || 'medium'}
        />

        {/* Tabs */}
        <div style={styles.tabBar}>
          {/* Special "Equipped" tab — shows the equip page; only available
              when the caller wired up onEquip (player view, not DM). */}
          {onEquip && (
            <button
              key="equipped"
              style={{ ...styles.tab, ...(activeTab === 'equipped' ? styles.activeTab : {}) }}
              onClick={() => { setActiveTab('equipped'); setAdding(false); setCustomMode(false); setEditingId(null); }}
            >
              <span style={styles.tabIcon}>🎽</span>
              <span style={styles.tabLabel}>Equipped</span>
            </button>
          )}
          {INVENTORY_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              style={{ ...styles.tab, ...(activeTab === cat.id ? styles.activeTab : {}) }}
              onClick={() => { setActiveTab(cat.id); setAdding(false); setCustomMode(false); setEditingId(null); }}
            >
              <span style={styles.tabIcon}>{cat.icon}</span>
              <span style={styles.tabLabel}>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={styles.body}>
          {activeTab === 'equipped' ? (
            <EquipTab playerRecord={playerRecord} onEquip={onEquip} editable={editable} />
          ) : (
            <>
              {tabItems.length === 0 && !adding && (
                <div style={styles.empty}>
                  No {INVENTORY_CATEGORIES.find((c) => c.id === activeTab).label.toLowerCase()} yet
                  {editable && ` — tap + Add ${INVENTORY_CATEGORIES.find((c) => c.id === activeTab).label.toLowerCase()} to start.`}
                </div>
              )}

              {tabItems.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  expanded={editingId === item.id}
                  onExpand={() => setEditingId((cur) => (cur === item.id ? null : item.id))}
                  onChange={upsertItem}
                  onDelete={() => { removeItem(item.id); setEditingId(null); }}
                  editable={editable}
                  onEquip={onEquip}
                  onUseConsumable={onUseConsumable}
                />
              ))}

              {editable && (
                adding ? (
                  <AddItemPicker
                    category={activeTab}
                    customMode={customMode}
                    setCustomMode={setCustomMode}
                    onCancel={() => { setAdding(false); setCustomMode(false); }}
                    onCatalogPick={addFromCatalog}
                    onCustomSave={addCustom}
                  />
                ) : (
                  <button style={styles.addBtn} onClick={() => setAdding(true)}>
                    + Add {INVENTORY_CATEGORIES.find((c) => c.id === activeTab).label.toLowerCase().replace(/s$/, '')}
                  </button>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Wallet row ─────────────────────────────────────────────────────────────────
function CarryMeter({ inventory, strScore, sizeCategory = 'medium' }) {
  const wt   = currentWeight(inventory);
  const cap  = carryCapacity(strScore, sizeCategory);
  const tier = encumbranceTier(wt, strScore, sizeCategory);
  const meta = ENCUMBRANCE_LABELS[tier];
  // Bar fill — green to capacity, yellow past 1/3, orange past 2/3, red over cap.
  const pct  = Math.min(1, wt / cap);
  return (
    <div style={meterStyles.wrap}>
      <div style={meterStyles.label}>
        <span style={meterStyles.icon}>🏋</span>
        <span style={meterStyles.title}>Carry</span>
        <span style={meterStyles.value}>{wt.toFixed(1)} / {cap} lb</span>
        <span style={{ ...meterStyles.tier, color: meta.color }}>{meta.short}</span>
      </div>
      <div style={meterStyles.barWrap}>
        <div style={{ ...meterStyles.barFill, width: `${pct * 100}%`, background: meta.color }} />
      </div>
    </div>
  );
}

const meterStyles = {
  wrap:   { padding: '6px 12px', borderBottom: '1px solid rgba(74,61,40,0.6)', display: 'flex', flexDirection: 'column', gap: 4, background: 'rgba(212,168,71,0.04)' },
  label:  { display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 11, color: '#9a8b6e', fontWeight: 700 },
  icon:   { fontSize: 13 },
  title:  { textTransform: 'uppercase', letterSpacing: '0.06em' },
  value:  { color: '#f0e6d0', fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontFamily: 'system-ui, sans-serif' },
  tier:   { marginLeft: 'auto', fontSize: 10, fontStyle: 'italic', fontFamily: 'system-ui, sans-serif' },
  barWrap:{ height: 4, background: 'rgba(0,0,0,0.4)', borderRadius: 2, overflow: 'hidden' },
  barFill:{ height: '100%', transition: 'width 0.3s, background 0.3s' },
};

function Wallet({ currency, editable, setCoin }) {
  return (
    <div style={styles.wallet}>
      <span style={styles.walletLabel}>💰 Wallet</span>
      <div style={styles.walletCells}>
        {CURRENCY_DENOMS.map((d) => (
          <CoinCell key={d.id} denom={d} value={currency?.[d.id] || 0} editable={editable} setCoin={setCoin} />
        ))}
      </div>
      <span style={styles.walletTotal}>≈ {totalInGp(currency)} gp</span>
    </div>
  );
}

function CoinCell({ denom, value, editable, setCoin }) {
  const [editing, setEditing] = useState(false);
  const [input,   setInput]   = useState(String(value));
  const commit = () => {
    const n = parseInt(input, 10);
    setCoin(denom.id, isNaN(n) ? 0 : n);
    setEditing(false);
  };
  if (editing) {
    return (
      <div style={styles.coinCell}>
        <input
          autoFocus
          type="number" min="0"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          style={styles.coinInput}
        />
        <span style={styles.coinLabel}>{denom.label}</span>
      </div>
    );
  }
  return (
    <div style={styles.coinCell}>
      {editable && <button style={styles.coinStep} onClick={() => setCoin(denom.id, Math.max(0, value - 1))}>−</button>}
      <button
        style={styles.coinValue}
        disabled={!editable}
        onClick={() => { setInput(String(value)); setEditing(true); }}
        title="Tap to edit"
      >{value}</button>
      <span style={styles.coinLabel}>{denom.label}</span>
      {editable && <button style={styles.coinStep} onClick={() => setCoin(denom.id, value + 1)}>+</button>}
    </div>
  );
}

// ── Item row (collapsed / expanded edit form) ──────────────────────────────────
function ItemRow({ item, expanded, onExpand, onChange, onDelete, editable, onEquip, onUseConsumable }) {
  const showEquip = EQUIPPABLE_CATEGORIES.has(item.category);
  // Look up catalog metadata for the Use button — only consumables with a
  // mechanical hook (heal/buff) get the action button.
  const cat       = INVENTORY_CATALOG.find((c) => c.category === item.category && c.name === item.name);
  const canUse    = !!onUseConsumable && item.category === 'consumable' && cat?.use && cat.use.type !== null && (item.qty || 0) > 0;

  return (
    <div style={{ ...styles.itemRow, ...(expanded ? styles.itemRowExpanded : {}) }}>
      <button style={styles.itemHeader} onClick={onExpand} disabled={!editable && !item.description}>
        <span style={styles.itemName}>{item.name || '(unnamed)'}</span>
        {item.qty > 1 && <span style={styles.qtyBadge}>×{item.qty}</span>}
        {showEquip && item.equipped && <span style={styles.equippedPill}>Equipped</span>}
        {canUse && (
          <span
            style={styles.useBtn}
            onClick={(e) => { e.stopPropagation(); onUseConsumable(item.id); }}
          >
            🍶 Use
          </span>
        )}
        {showEquip && editable && onEquip && (
          <span
            style={{ ...styles.equipBtn, ...(item.equipped ? styles.equipBtnActive : {}) }}
            onClick={(e) => { e.stopPropagation(); onEquip(item.id); }}
            title={item.equipped ? 'Unequip' : 'Equip'}
          >
            {item.equipped ? '✓' : 'Equip'}
          </span>
        )}
      </button>
      {!expanded && (() => {
        const w = (item.weightLb != null) ? item.weightLb : (INVENTORY_CATALOG.find((c) => c.name === item.name)?.weightLb ?? 0);
        const wTag = w > 0 ? `🏋 ${w} lb` : null;
        if (!item.description && !wTag) return null;
        return (
          <div style={styles.itemDesc}>
            {item.description}
            {item.description && wTag && <span style={{ color: '#6a5d45' }}> · </span>}
            {wTag && <span style={{ color: '#9a8b6e' }}>{wTag}</span>}
          </div>
        );
      })()}
      {expanded && (
        <ItemEditForm
          item={item}
          onChange={onChange}
          onDelete={onDelete}
          editable={editable}
          showEquip={showEquip}
        />
      )}
    </div>
  );
}

function ItemEditForm({ item, onChange, onDelete, editable, showEquip }) {
  const [draft, setDraft] = useState(item);
  React.useEffect(() => setDraft(item), [item.id]); // reset on row swap
  const update = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onChange(next);
  };
  return (
    <div style={styles.editForm}>
      <input
        style={styles.input}
        type="text"
        value={draft.name}
        onChange={(e) => update({ name: e.target.value })}
        placeholder="Item name"
        disabled={!editable}
      />
      <textarea
        style={styles.textarea}
        rows={2}
        value={draft.description}
        onChange={(e) => update({ description: e.target.value })}
        placeholder="Description (optional)"
        disabled={!editable}
      />
      <div style={styles.editControls}>
        <span style={styles.qtyStepper}>
          <button style={styles.stepBtn} onClick={() => update({ qty: Math.max(0, (draft.qty || 0) - 1) })} disabled={!editable}>−</button>
          <span style={styles.qtyValue}>{draft.qty || 0}</span>
          <button style={styles.stepBtn} onClick={() => update({ qty: (draft.qty || 0) + 1 })} disabled={!editable}>+</button>
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9a8b6e' }}>
          🏋 Weight
          <input
            type="number"
            min={0}
            step={0.5}
            value={draft.weightLb ?? 0}
            onChange={(e) => {
              const v = Number(e.target.value);
              update({ weightLb: Number.isFinite(v) && v >= 0 ? v : 0 });
            }}
            disabled={!editable}
            style={{ width: 64, padding: '4px 6px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#f0e6d0', fontSize: 12, textAlign: 'right' }}
          />
          <span style={{ color: '#6a5d45' }}>lb</span>
        </label>
        {showEquip && (
          <label style={styles.equipToggle}>
            <input
              type="checkbox"
              checked={!!draft.equipped}
              onChange={(e) => update({ equipped: e.target.checked })}
              disabled={!editable}
            />
            Equipped
          </label>
        )}
        {editable && (
          <button style={styles.deleteBtn} onClick={onDelete} title="Delete item">🗑</button>
        )}
      </div>
    </div>
  );
}

// ── Add picker (catalog + custom) ──────────────────────────────────────────────
function AddItemPicker({ category, customMode, setCustomMode, onCancel, onCatalogPick, onCustomSave }) {
  const catalog = useMemo(() => catalogFor(category), [category]);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((it) =>
      it.name.toLowerCase().includes(q) ||
      (it.description || '').toLowerCase().includes(q)
    );
  }, [catalog, search]);

  if (customMode) {
    return <CustomItemForm category={category} onCancel={onCancel} onSave={onCustomSave} />;
  }

  return (
    <div style={styles.picker}>
      <div style={styles.pickerHeader}>
        <input
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
          autoFocus
        />
        <button style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
      </div>
      <div style={styles.catalogList}>
        {filtered.length === 0 && <div style={styles.empty}>No matches in catalog.</div>}
        {filtered.map((entry) => (
          <button
            key={entry.name}
            style={styles.catalogRow}
            onClick={() => onCatalogPick(entry)}
          >
            <span style={styles.catalogName}>{entry.name}</span>
            {entry.description && <span style={styles.catalogDesc}>{entry.description}</span>}
          </button>
        ))}
      </div>
      <button style={styles.customBtn} onClick={() => setCustomMode(true)}>
        ✏ Custom item
      </button>
    </div>
  );
}

function CustomItemForm({ category, onCancel, onSave }) {
  const [name, setName] = useState('');
  const [qty,  setQty]  = useState(1);
  const [desc, setDesc] = useState('');
  const [weight, setWeight] = useState(0);
  const [equipped, setEquipped] = useState(false);
  const showEquip = EQUIPPABLE_CATEGORIES.has(category);
  const submit = () => {
    if (!name.trim()) return;
    const w = Number(weight);
    onSave({
      name: name.trim(),
      qty: Math.max(0, qty | 0),
      description: desc.trim(),
      weightLb: Number.isFinite(w) && w >= 0 ? w : 0,
      equipped: showEquip ? equipped : false,
    });
  };
  return (
    <div style={styles.picker}>
      <input
        style={styles.input}
        type="text"
        placeholder="Item name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <textarea
        style={styles.textarea}
        rows={2}
        placeholder="Description (optional)"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      <div style={styles.editControls}>
        <span style={styles.qtyStepper}>
          <button style={styles.stepBtn} onClick={() => setQty(Math.max(0, qty - 1))}>−</button>
          <span style={styles.qtyValue}>{qty}</span>
          <button style={styles.stepBtn} onClick={() => setQty(qty + 1)}>+</button>
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9a8b6e' }}>
          🏋 Weight
          <input
            type="number"
            min={0}
            step={0.5}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            style={{ width: 64, padding: '4px 6px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#f0e6d0', fontSize: 12, textAlign: 'right' }}
          />
          <span style={{ color: '#6a5d45' }}>lb</span>
        </label>
        {showEquip && (
          <label style={styles.equipToggle}>
            <input type="checkbox" checked={equipped} onChange={(e) => setEquipped(e.target.checked)} />
            Equipped
          </label>
        )}
      </div>
      <div style={styles.formActions}>
        <button style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
        <button style={styles.saveBtn} onClick={submit} disabled={!name.trim()}>Save</button>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  panel:   { width: '100%', maxWidth: 520, maxHeight: '92vh', background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: 'rgba(0,0,0,0.2)' },
  title:   { fontSize: 16, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  closeBtn:{ background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 20, cursor: 'pointer', padding: '0 4px' },
  tradeBtn:{ background: 'rgba(212,168,71,0.15)', border: '1px solid #8b7340', borderRadius: 4, color: '#d4a847', padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em', marginRight: 6 },

  // Wallet
  wallet:        { padding: '8px 12px', borderBottom: '1px solid rgba(74,61,40,0.6)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, background: 'rgba(212,168,71,0.06)' },
  walletLabel:   { fontSize: 11, color: '#d4a847', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' },
  walletCells:   { display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 },
  walletTotal:   { fontSize: 11, color: '#9a8b6e', fontWeight: 700 },
  coinCell:      { display: 'inline-flex', alignItems: 'center', gap: 2, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, padding: '2px 4px' },
  coinStep:      { width: 22, height: 22, background: 'transparent', border: 'none', color: '#d4a847', fontSize: 14, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1 },
  coinValue:     { minWidth: 28, padding: '0 4px', background: 'transparent', border: 'none', color: '#f0e6d0', fontSize: 13, fontWeight: 800, cursor: 'pointer' },
  coinLabel:     { fontSize: 9, color: '#9a8b6e', fontWeight: 700, padding: '0 2px' },
  coinInput:     { width: 50, background: '#142038', border: '1px solid #d4a847', borderRadius: 3, color: '#f0e6d0', padding: '2px 4px', fontSize: 13, fontWeight: 700, outline: 'none', textAlign: 'center' },

  // Tabs
  tabBar:    { display: 'flex', borderBottom: '2px solid #8b7340', flexShrink: 0 },
  tab:       { flex: 1, background: 'transparent', border: 'none', borderRadius: 0, padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, color: '#9a8b6e', cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  activeTab: { color: '#d4a847', borderBottom: '2px solid #d4a847', background: 'rgba(212,168,71,0.06)' },
  tabIcon:   { fontSize: 16, lineHeight: 1 },
  tabLabel:  { fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' },

  // Body / items
  body:      { flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  empty:     { textAlign: 'center', color: '#6a5d45', fontSize: 12, padding: '16px 8px', fontStyle: 'italic' },
  itemRow:   { background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4 },
  itemRowExpanded: { borderColor: '#d4a847', background: '#142038' },
  itemHeader:{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' },
  itemName:  { fontSize: 13, fontWeight: 700, color: '#f0e6d0', flex: 1 },
  qtyBadge:  { fontSize: 11, fontWeight: 800, color: '#1a0e00', background: '#facc15', borderRadius: 8, padding: '0 6px', minWidth: 22, textAlign: 'center' },
  equippedPill: { fontSize: 10, fontWeight: 700, color: '#86efac', background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.4)', borderRadius: 3, padding: '1px 6px' },
  useBtn:       { fontSize: 11, fontWeight: 700, color: '#7ab8e0', background: 'rgba(122,184,224,0.15)', border: '1px solid rgba(122,184,224,0.5)', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  equipBtn:     { fontSize: 11, fontWeight: 700, color: '#d4a847', background: 'rgba(212,168,71,0.12)', border: '1px solid rgba(212,168,71,0.4)', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em' },
  equipBtnActive: { color: '#86efac', background: 'rgba(74,222,128,0.18)', border: '1px solid rgba(74,222,128,0.6)' },
  itemDesc:  { fontSize: 11, color: '#9a8b6e', padding: '0 10px 8px', lineHeight: 1.4 },

  // Edit form
  editForm:  { padding: '4px 10px 10px', display: 'flex', flexDirection: 'column', gap: 6 },
  input:     { background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  textarea:  { background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 12, outline: 'none', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.4 },
  editControls: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  qtyStepper:{ display: 'inline-flex', alignItems: 'center', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4 },
  stepBtn:   { width: 28, height: 28, background: 'transparent', border: 'none', color: '#d4a847', fontSize: 16, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1 },
  qtyValue:  { minWidth: 28, textAlign: 'center', fontSize: 13, fontWeight: 800, color: '#f0e6d0' },
  equipToggle: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#9a8b6e', cursor: 'pointer' },
  deleteBtn: { marginLeft: 'auto', width: 32, height: 32, background: 'rgba(192,51,51,0.15)', border: '1px solid rgba(192,51,51,0.5)', borderRadius: 4, color: '#f87171', fontSize: 14, cursor: 'pointer', padding: 0 },

  // Add picker
  addBtn:    { width: '100%', padding: '10px', background: 'rgba(212,168,71,0.1)', border: '1px dashed #8b7340', borderRadius: 4, color: '#d4a847', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em' },
  picker:    { display: 'flex', flexDirection: 'column', gap: 6, padding: 8, background: '#0a1426', border: '1px solid #d4a847', borderRadius: 4 },
  pickerHeader: { display: 'flex', gap: 6 },
  searchInput: { flex: 1, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 13, outline: 'none' },
  cancelBtn: { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  catalogList: { maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 },
  catalogRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '6px 8px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', cursor: 'pointer', textAlign: 'left' },
  catalogName: { fontSize: 13, fontWeight: 700, color: '#f0e6d0' },
  catalogDesc: { fontSize: 11, color: '#9a8b6e', lineHeight: 1.3 },
  customBtn: { padding: '8px', background: 'rgba(122,87,184,0.15)', border: '1px solid rgba(122,87,184,0.5)', borderRadius: 4, color: '#c9b3e8', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 },
  saveBtn:   { background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', color: '#1a0e00', padding: '4px 12px', fontSize: 12, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4 },
};
